import { useEffect, useRef, useState } from "react";
import { api, ApiError, navigate, paymentTarget, useApi } from "./api";
import { Empty, Loading, Notice, PageTitle } from "./common";
import { money, plainText } from "./format";
import ProductDetails from "./ProductDetails";
import { useProductCatalog } from "./useProductCatalog";
import { deliveryLabel, productTitle, stockLabel } from "./product";
import type { Coupon, Order, PaymentMethod, Preview } from "./types";
import { analyticsContext, trackEvent } from "./analytics";

export function quoteChoice(preview: Preview) {
  const coupon = preview.coupon
    ? { userCouponId: preview.coupon.userCouponId || preview.coupon.id }
    : { disableAutoCoupon: true };
  return { ...coupon, ...(preview.invoiceRequested ? { invoiceRequested: true } : {}) };
}
export default function Checkout({ planId }: { planId: string }) {
  const catalog = useProductCatalog(),
    methods = useApi<PaymentMethod[]>("/epay/methods?planId=" + encodeURIComponent(planId), catalog.data?.version);
  const coupons = useApi<Coupon[]>(
    "/user/coupons?planId=" + encodeURIComponent(planId),
  );
  const plan = catalog.data?.plans.find((p) => p.id === planId);
  const selectionTitle = plan ? productTitle(plan) : "";
  const specName = plan?.specName ?? plan?.period;
  const description = plainText(plan?.description || plan?.detail);
  const delivery = plan ? deliveryLabel(plan) : "";
  const [choice, setChoice] = useState("auto"),
    [invoiceRequested, setInvoiceRequested] = useState(false),
    [preview, setPreview] = useState<Preview | null>(null),
    [previewError, setPreviewError] = useState(""),
    [previewBusy, setPreviewBusy] = useState(true),
    [refresh, setRefresh] = useState(0);
  const [payType, setPayType] = useState(""),
    [agreed, setAgreed] = useState(false),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [uncertain, setUncertain] = useState(false);
  const [created, setCreated] = useState<{
      payUrl: string;
      orderNo: string;
      order: Order;
    } | null>(null),
    locked = useRef(false);
  const enabledMethods = (methods.data || []).filter((m) => m.enabled);
  const effectiveType = enabledMethods.some((m) => m.type === payType)
    ? payType
    : enabledMethods[0]?.type || "";
  useEffect(() => {
    if (!plan || created) return;
    const controller = new AbortController();
    setPreview(null);
    setPreviewError("");
    setPreviewBusy(true);
    api<Preview>(
      "/epay/preview",
      {
        planId,
        ...(invoiceRequested ? { invoiceRequested: true } : {}),
        ...(choice === "none"
          ? { disableAutoCoupon: true }
          : choice === "auto"
            ? {}
            : { userCouponId: Number(choice) }),
      },
      controller.signal,
    )
      .then((next) => {
        if (!!next.invoiceRequested !== invoiceRequested)
          throw new Error("开票报价暂未确认，请重新计算或取消勾选后继续。");
        if (!controller.signal.aborted) setPreview(next);
      })
      .catch((e) => {
        if (!controller.signal.aborted) setPreviewError(e.message);
      })
      .finally(() => {
        if (!controller.signal.aborted) setPreviewBusy(false);
      });
    return () => controller.abort();
  }, [planId, choice, invoiceRequested, refresh, plan?.displayAmount, plan?.inStock, plan?.enabled, plan?.orderable]);
  async function submit() {
    if (locked.current || !preview || previewBusy || !effectiveType || !agreed || !plan ||
        catalog.error || methods.loading || methods.error || !plan.orderable || !plan.inStock ||
        !!preview.invoiceRequested !== invoiceRequested)
      return;
    locked.current = true;
    setBusy(true);
    setError("");
    try {
      const result = await api<{
        payUrl: string;
        orderNo: string;
        order: Order;
      }>("/epay/create", {
        planId,
        payType: effectiveType,
        ...quoteChoice(preview),
        analyticsContext: analyticsContext(),
      });
      if (!result.orderNo)
        throw new Error("订单结果异常，请先到我的订单确认。");
      setCreated({
        ...result,
        payUrl: result.payUrl ? paymentTarget(result.payUrl) : "",
      });
    } catch (e) {
      setError((e as Error).message);
      if (!(e instanceof ApiError) || e.status === 0 || e.status >= 500) {
        setUncertain(true);
      } else {
        locked.current = false;
        if (e.status === 409) {
          catalog.reload();
          setRefresh((n) => n + 1);
        }
      }
    } finally {
      setBusy(false);
    }
  }
  if (created)
    return (
      <>
        <PageTitle
          title="订单已创建"
          subtitle="核对订单金额后，前往收银台完成支付。"
        />
        <section className="panel checkout-created">
          <span className="success-mark">✓</span>
          <h2>{created.order?.planName || selectionTitle}</h2>
          <p className="muted">订单号 {created.orderNo}</p>
          <strong className="checkout-total">
            {money(created.order?.displayAmount)}
          </strong>
          {created.order?.invoiceRequested && <p className="muted">
            需要开发票 · 已含开票服务费 {money(created.order.invoiceFeeDisplayAmount)}
          </p>}
          {created.order?.displayAmount !== preview?.displayAmount && (
            <Notice kind="info">金额已更新，请以此订单金额为准。</Notice>
          )}
          <div className="actions">
            {created.payUrl && (
              <a className="button primary" href={created.payUrl} onClick={() => { void trackEvent("payment_started"); }}>
                立即支付
              </a>
            )}
            <button
              className="button subtle"
              onClick={() =>
                navigate("/orders/" + encodeURIComponent(created.orderNo))
              }
            >
              查看订单
            </button>
          </div>
        </section>
      </>
    );
  if (catalog.loading) return <Loading />;
  if (catalog.error && !catalog.data)
    return (
      <>
        <Notice>{catalog.error}</Notice>
        <button className="button primary" onClick={catalog.reload}>
          重新加载
        </button>
      </>
    );
  if (!plan)
    return (
      <Empty
        title="此套餐暂不可用"
        text="商品可能已调整，请返回选择其他套餐。"
        action={
          <button
            className="button primary"
            onClick={() => navigate("/subscribe")}
          >
            选择套餐
          </button>
        }
      />
    );
  return (
    <>
      <PageTitle
        title="确认订单"
        subtitle="请确认套餐说明、交付方式与实际应付金额。"
        action={
          <button
            className="button subtle"
            onClick={() => navigate("/subscribe")}
          >
            重新选购
          </button>
        }
      />
      {catalog.error && <Notice kind="info">{catalog.error}</Notice>}
      <div className="checkout-grid">
        <div className="checkout-selection">
          <section className="panel selection-card" aria-label="已选商品">
            <span className="selection-kicker">YOUR SELECTION</span>
            <header className="selection-heading">
              <div className="selection-product">
                <h2>{selectionTitle}</h2>
                {specName && <p className="selection-spec">{specName}</p>}
              </div>
              <dl className="selection-price">
                <dt>商品价格</dt>
                <dd>{money(plan.displayAmount)}</dd>
              </dl>
            </header>
            {description && <p className="product-description">{description}</p>}
            <dl className="selection-facts">
              <div><dt>数量</dt><dd>1 份</dd></div>
              <div><dt>库存</dt><dd>{stockLabel(plan)}</dd></div>
              {delivery && <div><dt>交付方式</dt><dd>{delivery}</dd></div>}
              {(Number(plan.minPurchaseQuantity) > 1 || Number(plan.maxPurchaseQuantity) > 0) && <>
                <div className="selection-limits">
                  <dt>购买限制</dt>
                  <dd>{plan.minPurchaseQuantity || 1} 份起购{Number(plan.maxPurchaseQuantity) > 0 ? `，每单最多 ${plan.maxPurchaseQuantity} 份` : ""}</dd>
                </div>
              </>}
            </dl>
            <ProductDetails content={plan.productContent ?? plan.instructions} />
          </section>
        </div>
        <section className="panel stack">
          <h2>支付详情</h2>
          <label>
            优惠券
            <select
              value={choice}
              onChange={(e) => setChoice(e.target.value)}
              disabled={busy}
            >
              <option value="auto">自动选择可用优惠</option>
              <option value="none">不使用优惠券</option>
              {(coupons.data || [])
                .filter(
                  (c) => c.usable !== false && c.effectiveStatus === "unused",
                )
                .map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
            </select>
          </label>
          {coupons.error && <Notice>{coupons.error}</Notice>}
          <label className={`check invoice-option${invoiceRequested ? " is-selected" : ""}`}>
            <input
              type="checkbox"
              name="invoice"
              checked={invoiceRequested}
              onChange={(event) => setInvoiceRequested(event.target.checked)}
              disabled={busy}
            />
            <span>需要开发票（按订单总额 7%，最低 ¥40）</span>
          </label>
          {previewBusy ? (
            <Loading />
          ) : previewError ? (
            <>
              <Notice>{previewError}</Notice>
              <button
                className="button subtle"
                onClick={() => setRefresh((n) => n + 1)}
              >
                重新计算
              </button>
            </>
          ) : (
            preview && (
              <dl className="details-grid">
                <dt>商品金额</dt>
                <dd>{money(preview.originalDisplayAmount)}</dd>
                <dt>优惠{preview.coupon ? " · " + preview.coupon.name : ""}</dt>
                <dd>−{money(preview.discountDisplayAmount)}</dd>
                {preview.invoiceRequested && <>
                  <dt>开票服务费</dt>
                  <dd>+{money(preview.invoiceFeeDisplayAmount)}</dd>
                </>}
                <dt>应付金额</dt>
                <dd className="checkout-total">
                  {money(preview.displayAmount)}
                </dd>
              </dl>
            )
          )}
          <fieldset className="payment-methods">
            <legend>支付方式</legend>
            {enabledMethods.map((m) => (
              <label className="payment-option" key={m.type}>
                <input
                  name="payment"
                  type="radio"
                  checked={effectiveType === m.type}
                  onChange={() => setPayType(m.type)}
                />
                {m.name}
              </label>
            ))}
            {methods.loading ? (
              <Loading />
            ) : methods.error ? (
              <Notice>{methods.error}</Notice>
            ) : (
              !enabledMethods.length && (
                <Notice>支付通道暂不可用，请稍后重试。</Notice>
              )
            )}
          </fieldset>
          <label className="check">
            <input
              type="checkbox"
              name="agreement"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
            />
            <span>
              我已核对套餐及使用说明，并同意
              <a href="/terms-of-service" target="_blank">
                服务条款
              </a>
              。
            </span>
          </label>
          <Notice>{error}</Notice>
          {uncertain ? (
            <>
              <Notice kind="info">
                此次提交可能已生成订单。请先检查我的订单，避免重复购买。
              </Notice>
              <button
                className="button primary"
                onClick={() => navigate("/transaction-record")}
              >
                检查我的订单
              </button>
            </>
          ) : (
            <button
              className="button primary full"
              disabled={
                busy ||
                previewBusy ||
                !preview ||
                !!preview.invoiceRequested !== invoiceRequested ||
                !agreed ||
                !effectiveType ||
                methods.loading ||
                !!methods.error ||
                !!catalog.error ||
                !plan.orderable ||
                !plan.inStock
              }
              onClick={submit}
            >
              {busy ? "正在创建订单…" : "创建订单"}
            </button>
          )}
          <p className="fine-print">支付完成后，可在我的订单查看交付内容。</p>
        </section>
      </div>
    </>
  );
}
